// Copy and presentation rules for the exit-reason question: the screen a studio sees
// when it tells us why it is leaving.
//
// Andrew approved this flow on 2026-10-01 (card exit-reason-prompt, round 3) with four
// changes, and all four are reflected here:
//   1. "Our season ended" is gone. His reasoning: a March to July season means most of
//      those studios are coming back, so that answer told us nothing.
//   2. The entry inside the account is a cancellation REQUEST that reaches us by email,
//      not a self-serve cancel button.
//   3. That entry is findable but not prominent. His words: "not obvious, not hidden".
//   4. The save offer is its own screen and is deliberately not in this file.
//
// The six reasons themselves are NOT here. They are served by the backend from
// server/src/constants/exitReason.js so the sentence a studio reads and the code stored
// in the database can never drift apart. A second copy of that list in the front end
// would be exactly that drift, so there is not one, not even as an offline fallback.

// Mirrors EXIT_REASON_COMMENT_MAX on the server. Held here as well so the box stops
// accepting characters at the limit, rather than letting a studio write a long note and
// then refusing it after they press send.
export const EXIT_REASON_COMMENT_MAX = 2000;

export const QUESTION_HEADING = 'Before you go, one question.';
export const QUESTION_INTRO =
    'It takes about ten seconds and it genuinely changes what we build next.';
export const COMMENT_LABEL = 'Anything else we should know? (optional)';
export const FOOTNOTE = 'Your schedules stay on your account either way.';

// The standalone page is reached from a link in our cancellation reply, so by the time a
// studio is reading it the cancellation has already happened and this is only the
// question. The in-account entry IS the request, so its button says so.
export const SEND_LABEL = {
    goodbye: 'Send',
    account: 'Send cancellation request',
};

// On the standalone page, skipping is a real option: nothing is owed, the answer is a
// favour. Inside the account there is no "Skip", because skipping there would send
// nothing at all, and a screen that then said "your cancellation request is with us"
// would be telling a studio something untrue. "Another reason" is on the list for anyone
// who does not want to say more, so nobody has to explain themselves to cancel.
export const SKIP_LABEL = 'Skip';
export const DISMISS_LABEL = 'Never mind';

export const CLOSING_HEADING = 'Thank you. That helps.';
export const CLOSING_BODY = {
    goodbye:
        'Your account is cancelled and your schedules, classes and students are kept. ' +
        'If you sign up again with this same email address, your data comes back with you.',
    // In-account the studio has only asked; nothing is cancelled until we confirm it, so
    // the "your data is kept" line is left off here. Andrew, 2026-10-09.
    account:
        'Your cancellation request is with us and we will confirm by email within one ' +
        'business day.',
};

export const CONTACT_LINE = 'Questions any time:';
export const HELP_EMAIL = 'info@studioshuffle.com';
export const HELP_PHONE = '(614) 321-7555';

// Shown instead of the question if the reason list cannot be loaded. It gives a studio a
// way through to a person, which an empty screen does not.
export const REASONS_UNAVAILABLE =
    'We could not load the question just now, so nothing has been sent. Please email ' +
    'info@studioshuffle.com or call (614) 321-7555 and we will take it from there.';

// Matches EXIT_REASON_LAYER.UNKNOWN_REASON on the server, so a studio reads the same
// sentence whether the empty pick is caught here or there.
export const PICK_ONE_FIRST = 'Please choose one of the reasons listed';

export const SEND_FAILED =
    'We could not send that just now and nothing has been saved. Please try again, or ' +
    'email info@studioshuffle.com if it keeps happening.';

export const ACCOUNT_ENTRY_LABEL = 'Thinking of leaving? Tell us why';

// Where the quiet entry is allowed to appear, kept as a function so it can be checked
// without rendering anything. Two deliberate limits: an admin managing plans on this
// page is not a studio thinking of leaving, and a studio that has not picked a plan yet
// has nothing to cancel.
export const shouldShowAccountEntry = ({ isAdmin, subscription } = {}) =>
    !isAdmin && Array.isArray(subscription) && subscription.length > 0;
